import { transliterate } from 'transliteration';
import { tokenizeFoodText } from '../food-text-tokenize';
import { createFoodWordStemmer, FoodTextAnalyzerDeps } from './food-search-match';
import {
  createFoodWordDictionary, completeFoodQuery, lastWordStartIndex, CompletedFoodQuery, FoodWordDictionary,
} from './food-word-completion';
import { FOOD_WORD_DICTIONARY_TEXT } from './food-word-dictionary.generated';
import { SuggestionRow } from './food-suggestion-row';

/**
 * The words the food search finishes off a part-typed word against.
 *
 * Built the first time a search needs it and then kept: it holds thousands of words, never
 * changes, and is the same for every search.
 *
 * It holds only words from the GLOBAL food database, so it cannot complete a word that appears
 * only in a food this person saved themselves — a private recipe, or a brand too rare to have
 * earned a place. Those are still FOUND by typing them in full, because the dictionary decides
 * completions only, never which foods are searchable. Adding this device's own food names as a
 * second source is the obvious next step and is deliberately not in this first version.
 */
let foodWordDictionary: FoodWordDictionary | undefined = undefined;

function getFoodWordDictionary(): FoodWordDictionary {
  if (!foodWordDictionary) {
    foodWordDictionary = createFoodWordDictionary(FOOD_WORD_DICTIONARY_TEXT.split('\n'));
  }
  return foodWordDictionary;
}

/**
 * Finishes off the word somebody is still typing in the food search (#5319, #5410).
 *
 * The Foods page needs completion in two places that must agree: the on-device suggestion rows
 * shown when the server's suggestions are unavailable, and the ranking of a committed search. Both
 * go through this one object, so the word list, the analyzer and the rule for a word the person
 * already picked live in one place (df-d2e69a0d16ea).
 *
 * A plain class rather than an Angular service, so it loads under the node unit-test runner.
 */
export class FoodWordCompleter {
  /**
   * The analyzer the matcher compares food names with. Shared, so the page's name scoring and the
   * completion here always stem and fold the same way.
   */
  readonly analyzerDeps: FoodTextAnalyzerDeps = {
    stem: createFoodWordStemmer(),
    foldAccents: transliterate,
  };


  /** The last word of `text` as the tokenizer sees it, or undefined when there is none. */
  lastWordOf(text: string): string | undefined {
    return tokenizeFoodText(text, this.analyzerDeps.foldAccents).slice(-1)[0];
  }

  /**
   * The words a committed search for `text` compares against food names.
   *
   * `pickedWord` is the last word of a suggestion the person picked. While it is still the last
   * word of `text` it counts as finished, so it is offered no completions of its own -- otherwise
   * picking "chicken breast" would rank as if "breasts" had been typed too.
   */
  searchTextQuery(text: string, pickedWord: string | undefined): CompletedFoodQuery {
    const wordWasPicked = pickedWord !== undefined && pickedWord === this.lastWordOf(text);
    return completeFoodQuery(
      text, getFoodWordDictionary(), this.analyzerDeps, tokenizeFoodText, wordWasPicked ? 0 : undefined);
  }

  /**
   * The on-device word list's suggestions for `text`: the text with its last word finished off.
   * No counts -- the list knows words, not how many foods hold a line. Used when the server's
   * suggestions are slow, failing or unreachable (#5410).
   *
   * No picked word is passed: these rows are offered while the person is typing, and typing
   * clears any earlier pick.
   */
  deviceSuggestionRows(text: string): SuggestionRow[] {
    const completed = completeFoodQuery(text, getFoodWordDictionary(), this.analyzerDeps, tokenizeFoodText);
    // replace only the last WORD, the way the tokenizer decides what a word is -- the completions
    // were worked out for that word and no more of the text (#5319)
    const lastWordStart = lastWordStartIndex(text, this.analyzerDeps.foldAccents, tokenizeFoodText);
    if (lastWordStart < 0) {
      return [];
    }
    return completed.lastWordCompletions.map(word => ({ text: text.slice(0, lastWordStart) + word }));
  }

}
